import { useState } from "react"
import { DEV_PATHS, MODULES } from "@cookies/shared/catalog/display"
import { BuyToast, Panel, PurchaseLedger } from "../components/ui"
import { fmtCompact, toNum } from "../game/format"
import { useBakeryStore } from "../game/bakeryStore"
import ActiveProjectCard from "../components/ActiveProjectCard"
import LeaderboardTab from "./LeaderboardTab"
import Top5 from "../components/Top5"

// Bakery levels are infinite (backend thresholds grow exponentially past
// 20B with no Max cap), so progress is always valid — always render the
// live "to Lv N+1" bar from the authoritative snapshot trio.
export default function BakeryPage({ go }: { go: (t: "project" | "race") => void }) {
  const store = useBakeryStore()
  const snap = store.snapshot
  const connected = store.connected
  const buyToast = store.buyNotice !== null && (store.buyNotice.kind === "module" || store.buyNotice.kind === "buy") ? store.buyNotice : null
  const [section, setSection] = useState<"modules" | "board">("modules")
  return (
    <div className="cb-page">
      <Panel className="center">
        <h2 className="cb-h">🍪 Bakery</h2>
        {snap !== null ? (
          <div role="status" aria-live="polite">
            <p className="dim small" style={{ margin: 0 }}>
              Bakery Lv. {snap.bakery.level} · {fmtCompact(snap.bakery.lifetimeCookies ?? snap.bakery.cookies)} lifetime baked · {fmtCompact(snap.bakery.cookies)} bank · {fmtCompact(snap.bakery.cps)}/sec
            </p>
            {(() => {
              const level = snap.bakery.level ?? 1
              const lifetime = toNum(snap.bakery.lifetimeCookies ?? snap.bakery.cookies)
              const rawNext = toNum(snap.bakery.nextLevelCookies ?? "10000")
              const next = Number.isFinite(rawNext) && rawNext > 0 ? rawNext : lifetime
              const rawPct = toNum(snap.bakery.levelProgress ?? 0)
              const pct = Number.isFinite(rawPct) ? Math.max(0, Math.min(100, rawPct)) : 0
              const remaining = Number.isFinite(next) && Number.isFinite(lifetime) ? Math.max(0, next - lifetime) : 0
              return (
                <div style={{ marginTop: 8 }}>
                  <small className="dim">🍪 {fmtCompact(lifetime)}/{fmtCompact(next)} lifetime · {fmtCompact(remaining)} to Bakery Lv. {level + 1} · {Math.floor(pct)}%</small>
                  <div className="cb-track"><div className="cb-fill-gold" style={{ width: `${pct}%` }} /></div>
                </div>
              )
            })()}
          </div>
        ) : (
          <p className="dim small" style={{ margin: 0 }} role="status" aria-live="polite">
            Loading bakery…
          </p>
        )}
      </Panel>

      <BuyToast notice={buyToast} />
      <PurchaseLedger history={store.purchaseHistory} kinds={["module"]} />
      <div className="cb-row" role="tablist" aria-label="Bakery sections">
        <button type="button" role="tab" aria-selected={section === "modules"} className={section === "modules" ? "cb-buy" : "cb-buy ghost"} onClick={() => setSection("modules")}>Modules</button>
        <button type="button" role="tab" aria-selected={section === "board"} className={section === "board" ? "cb-buy" : "cb-buy ghost"} onClick={() => setSection("board")}>Leaderboard</button>
      </div>

      {section === "board" ? (
        <>
          <LeaderboardTab />
          <Top5 />
        </>
      ) : (
        <div className="cb-split">
          <div className="cb-stack">
            <h3 className="cb-sec" style={{ marginTop: 0 }}>Bakery Modules</h3>
            {snap === null ? (
              <div className="cb-panel center small" role="status">Loading…</div>
            ) : (
              MODULES.map((m) => {
                const mod = snap.modules?.find((x) => x.id === m.id)
                const lv = mod?.level ?? 0
                const locked = mod?.locked ?? (m.lockedLevel != null && snap.bakery.level < m.lockedLevel)
                const cost = mod ? toNum(mod.cost) : 0
                // Live affordability: same total the header/counter shows
                // (bank + elapsed*CPS + optimistic), never the stale bank.
                const cookies = store.shownCookies
                const affordable = cookies >= cost
                const maxed = mod !== undefined && mod.maxLevel !== undefined && lv >= mod.maxLevel
                const owned = lv > 0
                const pending = store.isPending("module", m.id)
                const reqId = mod?.requiresId ?? m.requiresId ?? ""
                const reqLv = mod?.requiresLevel ?? m.requiresLevel ?? 0
                const reqHave = reqId === "" ? 99 : (snap.modules?.find((x) => x.id === reqId)?.level ?? 0)
                const lockText = locked ? (reqId !== "" && reqHave < reqLv ? `Requires ${reqId} Lv ${reqLv} (${reqHave}/${reqLv})` : `Requires Bakery Level ${mod?.requiredBakeryLevel ?? m.lockedLevel}`) : ""
                const cardClass = locked ? "cb-card muted" : maxed ? "cb-card maxed" : pending ? "cb-card confirming" : affordable ? "cb-card" : "cb-card muted"
                return (
                  <div key={m.id} title={m.desc} className={cardClass} aria-busy={pending || undefined}>
                    <span className={pending ? "cb-card-ico cookie-pulse" : "cb-card-ico"} aria-hidden="true">{m.icon}</span>
                    <div className="cb-card-body">
                      <div className="cb-card-title-row">
                        <b>{m.name} · {owned ? `Owned Lv ${lv}` : `Lv ${lv}`}</b>
                        <span className="cb-effect-badge" title={m.desc}>{m.cps}</span>
                        {pending && <span className="cb-pill confirming" role="status">Buying…</span>}
                      </div>
                      <small className="cb-card-purpose" title={m.desc}>{m.desc}</small>
                      {reqId !== "" && <small className="dim">Needs {reqId} Lv {reqLv} ({reqHave}/{reqLv})</small>}
                      <small className={affordable || locked || maxed ? "cb-cost" : "cb-cost poor"}>{locked ? lockText : maxed ? "Maxed · " + m.cps : `Cost 🍪 ${cost.toLocaleString()}`}</small>
                    </div>
                    {locked ? (
                      <span className="cb-locked">Locked</span>
                    ) : maxed ? (
                      <span className="cb-max">Maxed</span>
                    ) : pending ? (
                      <button type="button" disabled aria-busy="true" title="Buying…" className="cb-buy confirming">
                        Buying…
                      </button>
                    ) : (
                      <button
                        type="button"
                        disabled={!connected || !affordable}
                        title={!connected ? "Not connected — retrying…" : !affordable ? `Need 🍪 ${cost.toLocaleString()}` : "Buy next level"}
                        className={connected && affordable ? "cb-buy" : "cb-buy off"}
                        onClick={() => store.buyModule(m.id)}
                      >
                        {affordable ? "Buy" : `${cost.toLocaleString()}`}
                      </button>
                    )}
                  </div>
                )
              })
            )}
          </div>

          <div className="cb-side">
            <h3 className="cb-sec" style={{ marginTop: 0 }}>Development Path</h3>
            {DEV_PATHS.map((p) => (
              <div key={p.id} className="cb-path">
                <span className="cb-card-ico" style={{ width: 36, height: 36, fontSize: 20 }}>{p.icon}</span>
                <span style={{ flex: 1 }}>
                  <b>{p.name}</b><br /><small className="dim">{p.desc}</small>
                </span>
              </div>
            ))}
            <ActiveProjectCard />
            <div className="cb-side-card">
              <div className="cb-row">
                <button type="button" className="cb-buy" onClick={() => go("project")}>View</button>
                <button type="button" className="cb-buy ghost" onClick={() => go("race")}>Race</button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}
